import { Injectable } from '@angular/core';
import {
    IAirCreateBookingRequest,
    IAirCreateBookingRequestFareFamily,
    IAirCreateBookingRequestPayment,
    IAirCreateBookingRequestPrice,
    IAirCreateBookingRequestSegment,
    IAirCreateBookingRequestTraveller
} from "libraries/service/resources/model/booking/air/IAirCreateBooking";
import {
    bookingActionConfiguration,
    EConditionKey,
    EModifyRequest,
    IBookingActionConfiguration,
    IBookingActionConfigurationItem,
    IBookingActionConfigurationItemAction,
    IBookingActionConfigurationItemCondition,
} from "libraries/service/resources/air-booking/booking-action-configuration";
import { Observable, of } from "rxjs";
import { concatMap, flatMap } from "rxjs/internal/operators";
import { IFareFamily } from "libraries/service/resources/booking/interfaces/IGetBookings";
import { AncillaryServicesService } from "app_locobook/domain/ancillaryServices/useCases/ancillary-services.service";
import { ResponseForConvertOfferAfx2jsonType, ServiceInOfferType, ServiceInTripType } from "@afidium/ts-facade";
import { CacheQuotedAvailabilityService } from "app_locobook/service/cache-quoted-availability/cache-quoted-availability.service";
import { AirQuoteMappingService } from "libraries/service/resources/airQuote/air-quote-mapping.service";
import { IQuote } from "libraries/service/resources/airTrip/IGetAirTripQuotes";
import { AirBookingRequesterHelperService } from "libraries/service/resources/air-booking/air-booking-requester-helper.service";
import { AirTravellerType } from "libraries/service/resources/model/booking/air/IAirTraveller";
import { tap } from "rxjs/operators";
import { ConfigurationResourceService } from "libraries/service/resources/configuration/configurationResource.service";
import { LocalizedPipe } from "libraries/pipes/localized/localized.pipe";
import { AirBookingCreatePaymentService } from "libraries/service/booking/air-booking-create-payment.service";

@Injectable()
export class AirBookingConditionApplyService {

    private finalRequest: IAirCreateBookingRequest;

    constructor(
        private readonly ancillaryServicesService: AncillaryServicesService,
        private readonly airQuoteMappingService: AirQuoteMappingService,
        private readonly cacheQuotedAvailabilityService: CacheQuotedAvailabilityService,
        private readonly airBookingRequesterHelperService: AirBookingRequesterHelperService,
        private readonly configurationResourceService: ConfigurationResourceService,
        private readonly localizedPipe: LocalizedPipe,
        private readonly createBookingPaymentStorageService: AirBookingCreatePaymentService,
    ) {
    }

    public airCreateBookingRequestTestAndModify(
        originalRequest: IAirCreateBookingRequest,
        fareFamily: {
            [key: string]: IFareFamily
        },
        segment: IAirCreateBookingRequestSegment,
        travellers: {
            [key: string]: IAirCreateBookingRequestTraveller
        },
        isLowCost?: boolean,
        // tslint:disable-next-line:no-any
    ): Observable<[IAirCreateBookingRequest, boolean]> {

        let bookingActionConfigurationCache: IBookingActionConfiguration;

        // return of(bookingActionConfiguration)
        return this.configurationResourceService.get(['wui', 'app_locobook', 'config', 'create-booking', 'create-booking.json'], bookingActionConfiguration)
            .pipe(
                //extract OnCreateBooking items
                flatMap((bookingActionConfigurationResponse: IBookingActionConfiguration): Observable<IBookingActionConfigurationItem> => {
                    this.finalRequest = originalRequest;
                    bookingActionConfigurationCache = bookingActionConfigurationResponse;
                    return of(...(this.putPaymentBeforeActionInLastPosition(bookingActionConfigurationCache.onCreateBookingArray)));
                }),
                // plays onCreateBooking items one after one, and define a 'isLast' boolean
                concatMap((onCreateBooking: IBookingActionConfigurationItem, index: number): Observable<[IAirCreateBookingRequest, boolean]> => {
                    if (index === 0) {
                        this.cacheQuotedAvailabilityService.clearServiceCache();
                    }
                    const isLast: boolean = index === bookingActionConfigurationCache.onCreateBookingArray.length - 1;
                    const isTrue: boolean = this.isTrueCondition(onCreateBooking, this.finalRequest, travellers, isLowCost);
                    if (isTrue) {
                        return this.doActionBefore(
                            onCreateBooking.before.isTrueActionArray[0],
                            this.finalRequest,
                            fareFamily,
                            segment,
                            travellers,
                            isLast,
                        );
                    }

                    return of([this.finalRequest, isLast]);
                }),
            );
    }

    private doActionBefore(
        action: IBookingActionConfigurationItemAction,
        originalRequest: IAirCreateBookingRequest,
        fareFamily: {
            [key: string]: IFareFamily
        },
        segment: IAirCreateBookingRequestSegment,
        travellers: {
            [key: string]: IAirCreateBookingRequestTraveller
        },
        isLast: boolean,
    ): Observable<[IAirCreateBookingRequest, boolean]> {
        if (action.emitErrorMessage) {
            throw new Error(this.localizedPipe.transform(action.emitErrorMessage));
        }
        if (action.modifyRequest) {
            return this.modifyRequestWith(
                originalRequest,
                fareFamily,
                segment,
                travellers,
                action.modifyRequest,
                isLast,
            );
        }
        return of([originalRequest, isLast]);
    }

    private isTrueCondition(
        onCreateBooking: IBookingActionConfigurationItem,
        originalRequest: IAirCreateBookingRequest,
        travellers: {
            [key: string]: IAirCreateBookingRequestTraveller
        },
        isLowCost: boolean,
    ): boolean {

        // if no conditions set, then do action
        if (!onCreateBooking.conditionArray?.length) {
            return true;
        }

        const testResultArray: boolean[] = onCreateBooking.conditionArray.map((condition: IBookingActionConfigurationItemCondition): boolean => {
            const subTestResult: boolean[] = Object.entries(condition)
                .map(([key, value]: [string, string | boolean]): boolean => {

                    switch (key) {
                        case EConditionKey.hostCode:
                            return value === originalRequest.host;
                        case EConditionKey.hasServiceArray:
                            return value === Boolean(originalRequest.serviceArray?.length);
                        case EConditionKey.isLowCostCompany:
                            return value === Boolean(isLowCost);
                        case EConditionKey.hasAllAdtTravellersWithDob:
                            const hasTravellerWithOutDob: IAirCreateBookingRequestTraveller = Object.values(travellers)
                                .find((traveller: IAirCreateBookingRequestTraveller): boolean => {
                                    return !traveller.birthDate && traveller.type === AirTravellerType.Adult;
                                });
                            return value === !Boolean(hasTravellerWithOutDob);
                        default:
                            return false;
                    }
                });
            // on sub level we do AND test, means we return true if there are only true items in array
            return !subTestResult.includes(false);
        });

        //on main level we do OR test, wich means we only need 1 true
        return testResultArray.includes(true);
    }

    private modifyRequestWith(
        originalRequest: IAirCreateBookingRequest,
        fareFamily: {
            [key: string]: IFareFamily
        },
        segment: IAirCreateBookingRequestSegment,
        travellers: {
            [key: string]: IAirCreateBookingRequestTraveller
        },
        modification: EModifyRequest,
        isLast: boolean,
    ): Observable<[IAirCreateBookingRequest, boolean]> {
        //get bookingKey, aka quote id
        const parts: string[] = window.location.pathname
            .split('search')[1]
            .split('/')
            .filter((part: string): boolean => !part.includes('air'));

        let bookingKey: string = parts.find((part: string): boolean => part.includes('Q_'));
        if (!bookingKey) {
            bookingKey = parts.find((part: string): boolean => part.includes('Q.'));
        }

        if (modification === EModifyRequest.insertPaymentInRequest) {
            return of([
                this.addPayment(originalRequest),
                isLast
            ]);
        }

        if (modification === EModifyRequest.insertServicesInRequest) {
            return this.ancillaryServicesService.getQuoteWithService(bookingKey, fareFamily, segment, travellers)
                .pipe(
                    flatMap((response: ResponseForConvertOfferAfx2jsonType): Observable<[IAirCreateBookingRequest, boolean]> => {
                        const booking: Partial<IQuote> = this.airQuoteMappingService.mapGqaNewToOld(response);

                        // always do this action
                        let request: IAirCreateBookingRequest = this.updateTransactionIdAndPrice(
                            originalRequest,
                            response,
                            booking,
                        );
                        this.createBookingPaymentStorageService.setPayment(this.createBookingPaymentStorageService.computePayment(booking, response));
                        // #35925 : removed, payment is added from the last stored, and re-inject in last
                        // if (modification === EModifyRequest.insertPaymentInRequest) {
                        //     // only if low cost or TravelFusion (?)
                        //     request = this.addPayment(request, booking, response);
                        // }
                        request = this.addServiceArray(request, response);

                        return of([request, isLast]);

                    }),
                    tap(([request, isLastArg]: [IAirCreateBookingRequest, boolean]): void => {
                        this.finalRequest = request;
                    })
                );
        }
    }

    private updateTransactionIdAndPrice(
        originalRequest: IAirCreateBookingRequest,
        response: ResponseForConvertOfferAfx2jsonType,
        booking: Partial<IQuote>
    ): IAirCreateBookingRequest {

        const price: IAirCreateBookingRequestPrice = {
            ...this.airBookingRequesterHelperService.getPrice(booking),
            value: this.createBookingPaymentStorageService.extractFullTotalPriceFromServiceSelection(response).value,
            total: this.createBookingPaymentStorageService.extractFullTotalPriceFromServiceSelection(response).value,
        };

        return {
            ...originalRequest,
            transactionId: response.offer.transactionId,
            price,
            fareFamily: booking.fareFamily as unknown as {
                [key: string]: IAirCreateBookingRequestFareFamily
            },
            segment: this.airBookingRequesterHelperService.simplifySegments(booking.segment),
        };
    }

    private addServiceArray(
        originalRequest: IAirCreateBookingRequest,
        response: ResponseForConvertOfferAfx2jsonType,
    ): IAirCreateBookingRequest {

        const offerServices: ServiceInOfferType[] = response.offer?.serviceArray;

        if (offerServices?.length) {

            originalRequest.serviceArray = offerServices.map((offerService: ServiceInOfferType): object => {
                const relatedTripService: ServiceInTripType = response.trip.serviceArray
                    .find((tripService: ServiceInTripType): boolean => tripService.xId === offerService.xRef);
                return {
                    quantity: offerService.quantity,
                    syscode: {
                        value: relatedTripService.syscode.value,
                    },
                    travellerArray: offerService.travellerArray,
                    journeyArray: relatedTripService.journeyArray,
                    sectorArray: relatedTripService.sectorArray,
                    xRef: offerService.xRef,
                    xCategory: relatedTripService.xCategory,
                };

            });

        }

        return originalRequest;
    }

    private addPayment(
        originalRequest: IAirCreateBookingRequest,
    ): IAirCreateBookingRequest {
        // retrieve last payment infos stored
        const payment: IAirCreateBookingRequestPayment = this.createBookingPaymentStorageService.getPayment();

        return {
            ...originalRequest,
            payment
        };

    }

    /**
     * be sure to have payment actions in last
     * https://projets2.afidium.com/issues/35925
     * @param onCreateBookingArray
     * @private
     */
    private putPaymentBeforeActionInLastPosition(onCreateBookingArray: IBookingActionConfigurationItem[]): IBookingActionConfigurationItem[] {

        const itemsWithBeforeInsertPayment: IBookingActionConfigurationItem[] = onCreateBookingArray
            .filter((item: IBookingActionConfigurationItem): boolean => {
                return Boolean(
                    item.before.isTrueActionArray.find((beforeItem: IBookingActionConfigurationItemAction): boolean => {
                        return beforeItem.modifyRequest === EModifyRequest.insertPaymentInRequest;
                    }));
            });

        if (itemsWithBeforeInsertPayment?.length) {
            const itemsWithNoBeforeInsertPayment: IBookingActionConfigurationItem[] = onCreateBookingArray
                .filter((item: IBookingActionConfigurationItem): boolean => {
                    return !Boolean(
                        item.before.isTrueActionArray.find((beforeItem: IBookingActionConfigurationItemAction): boolean => {
                            return beforeItem.modifyRequest === EModifyRequest.insertPaymentInRequest;
                        }));
                });

            return [
                ...itemsWithNoBeforeInsertPayment,
                ...itemsWithBeforeInsertPayment,
            ];
        }

        return onCreateBookingArray;
    }
}
